/**
 * Shared types + tiny helpers for MRP UI pages.
 *
 * Mirrors the shapes returned by /api/mrp/*. Keep field names in sync with
 * src/mrp/types.ts and src/api/mrp-routes.ts on the backend.
 */

import { apiFetch } from "@/lib/api";
import {
  formatPurchaseOrderListDate,
  formatPurchaseOrderTimestamp,
  parsePurchaseOrderDateTime,
  type ParsedPurchaseOrderDateTime,
} from "@/lib/mrpPurchaseOrderTimestamp";

export {
  formatPurchaseOrderListDate,
  formatPurchaseOrderTimestamp,
  parsePurchaseOrderDateTime,
  type ParsedPurchaseOrderDateTime,
};

export type MrpLocation = "sgs" | "suntronic" | "kodiak";

/** Vertical stack order matches forecast grid rows (Kodiak → SGS → Suntronic). */
export const MRP_LOCATIONS: { code: MrpLocation; label: string }[] = [
  { code: "kodiak", label: "Kodiak" },
  { code: "sgs", label: "SGS" },
  { code: "suntronic", label: "Suntronic" },
];

/**
 * `public.location.id` for each MRP code. Mirrors the server-side mapping in
 * `src/mrp/megaventory-purchase-order-create.ts#MRP_LOCATION_TO_SUPABASE_LOCATION_ID`.
 * The New transfer form posts these as `origin_location_id` / `destination_location_id`.
 */
export const MRP_LOCATION_PUBLIC_LOCATION_ID: Record<MrpLocation, number> = {
  sgs: 1,
  suntronic: 2,
  kodiak: 14,
};

export function mrpLocationLabelForCode(code: MrpLocation): string {
  return MRP_LOCATIONS.find((row) => row.code === code)?.label ?? code;
}

export function isMrpLocationCode(code: string): code is MrpLocation {
  return code === "sgs" || code === "suntronic" || code === "kodiak";
}

export function purchaseOrderLocationPublicLocationId(locationCode: string): number {
  if (isMrpLocationCode(locationCode)) {
    return MRP_LOCATION_PUBLIC_LOCATION_ID[locationCode];
  }
  return defaultPurchaseOrderTransferOriginPublicLocationId(locationCode);
}

export function purchaseOrderLocationLabel(locationCode: string): string {
  if (isMrpLocationCode(locationCode)) {
    return mrpLocationLabelForCode(locationCode);
  }
  return locationCode;
}

/** Trim and uppercase SKU or CPN before BoM search and deep links. */
export function normalizeSkuSearchInput(sku: string): string {
  return sku.trim().toUpperCase();
}

/** Megaventory ALT- alias prefix — strip for hub display only; keep stored SKU for API keys. */
export function stripAltTlaSkuPrefixForDisplay(sku: string): string {
  return sku.replace(/^ALT-/, "").trim();
}

/** TanStack Query mutation key — serializes Megaventory-backed PO field saves in the UI. */
export function mrpPurchaseOrderMegaventoryWriteMutationKey(purchaseOrderId: number): readonly [
  "mrp",
  "purchase-orders",
  number,
  "megaventory-write",
] {
  return ["mrp", "purchase-orders", purchaseOrderId, "megaventory-write"];
}

/** Default transfer-request origin when linking from a purchase order: the PO ship-to site. */
export function defaultPurchaseOrderTransferOriginPublicLocationId(poLocation: string): number {
  if (poLocation === "sgs" || poLocation === "suntronic" || poLocation === "kodiak") {
    return MRP_LOCATION_PUBLIC_LOCATION_ID[poLocation];
  }
  return MRP_LOCATION_PUBLIC_LOCATION_ID.sgs;
}

/** Default transfer-request destination: another MRP site so origin and destination differ. */
export function defaultPurchaseOrderTransferDestinationPublicLocationId(poLocation: string): number {
  if (poLocation === "sgs" || poLocation === "suntronic" || poLocation === "kodiak") {
    const otherSite = MRP_LOCATIONS.find((row) => row.code !== poLocation);
    return MRP_LOCATION_PUBLIC_LOCATION_ID[otherSite?.code ?? "suntronic"];
  }
  return MRP_LOCATION_PUBLIC_LOCATION_ID.suntronic;
}

/** Vertical stack / API location list for forecast fetch (single request for all sites). */
export const MRP_FORECAST_LOCATION_STACK_ORDER: MrpLocation[] = MRP_LOCATIONS.map((row) => row.code);

export const MRP_FORECAST_API_LOCATION_PARAM = MRP_FORECAST_LOCATION_STACK_ORDER.join(",");

/** Row from GET /api/mrp/locations (planning sites on `public.location`). */
export interface MrpPlanningLocationApiRow {
  id: number;
  code: string;
  display_name: string | null;
  sort_order: number;
  active: boolean;
  include_in_forecast: boolean;
  megaventory_id: number | null;
}

/** Preserve legacy grid stack ordering for the default three sites; others follow by code. */
export function compareMrpForecastStackOrder(a: string, b: string): number {
  const rank: Record<string, number> = { kodiak: 0, sgs: 1, suntronic: 2 };
  const ra = rank[a] ?? 100;
  const rb = rank[b] ?? 100;
  if (ra !== rb) return ra - rb;
  return a.localeCompare(b);
}

export type MrpSkuPlanningMode = "mrp" | "rop" | "build_plan" | "na" | "m2o" | "supplier_forecast";

const MRP_SKU_PLANNING_OPTION_ROWS: { value: MrpSkuPlanningMode; label: string }[] = [
  { value: "build_plan", label: "Build plan" },
  { value: "m2o", label: "Made to order" },
  { value: "mrp", label: "MRP" },
  { value: "na", label: "N/A" },
  { value: "rop", label: "Reorder point" },
  { value: "supplier_forecast", label: "Supplier forecast" },
];

/** Planning mode labels for parts master, sorted A–Z. */
export const MRP_SKU_PLANNING_OPTIONS = [...MRP_SKU_PLANNING_OPTION_ROWS].sort((left, right) =>
  left.label.localeCompare(right.label),
);

/** Matches `GET /api/mrp/skus?planning=not_set` for rows with null `planning_mode`. */
export const MRP_SKU_PLANNING_FILTER_NOT_SET = "not_set" as const;

export type MrpSkuPlanningFilterToken = MrpSkuPlanningMode | typeof MRP_SKU_PLANNING_FILTER_NOT_SET;

/** Planning filter chips on `/mrp/skus`, sorted A–Z (includes Not set). */
export const MRP_SKU_PLANNING_FILTER_OPTIONS: { value: MrpSkuPlanningFilterToken; label: string }[] = [
  ...MRP_SKU_PLANNING_OPTIONS,
  { value: MRP_SKU_PLANNING_FILTER_NOT_SET, label: "Not set" },
].sort((left, right) => left.label.localeCompare(right.label));

/** Inline `<select>` options for planning mode (`""` = not set), sorted A–Z. */
const mrpSkuPlanningSelectOptionRows: { value: MrpSkuPlanningMode | ""; label: string }[] = [
  { value: "", label: "Not set" },
  ...MRP_SKU_PLANNING_OPTIONS,
];
export const MRP_SKU_PLANNING_SELECT_OPTIONS = [...mrpSkuPlanningSelectOptionRows].sort((left, right) =>
  left.label.localeCompare(right.label),
);

/** Matches `GET /api/mrp/skus?category=none` for rows with null `mv_category_id`. */
export const MRP_SKU_CATEGORY_FILTER_NONE = "none" as const;

export interface MrpSkuMasterRow {
  sku: string;
  category: string | null;
  /** Megaventory product category id; null when not set. */
  mv_category_id: number | null;
  description: string | null;
  variant: string | null;
  mv_id: number | null;
  /** From public.sku.planning_mode; null when not set in the database. */
  planning_mode: MrpSkuPlanningMode | null;
  /** From public.sku.obsolete (list endpoint only; combobox/search may omit). */
  obsolete?: boolean;
}

/**
 * Subtab on `/mrp/skus/:sku` (optional `?tab=`; omitted means Properties).
 * Use `buildMrpSkuDetailPath` or the `MrpSkuDetailLink` component from any page so
 * encoding and query params stay consistent (shareable, bookmarkable URLs).
 */
export type MrpSkuDetailTab =
  | "properties"
  | "bom"
  | "inventory"
  | "aml"
  | "shopping"
  | "documents"
  | "quotes"
  | "transaction_history";

const MRP_SKU_DETAIL_TAB_SET: ReadonlySet<string> = new Set([
  "properties",
  "bom",
  "inventory",
  "aml",
  "shopping",
  "documents",
  "quotes",
  "transaction_history",
]);

export function isMrpSkuDetailTab(value: string | null | undefined): value is MrpSkuDetailTab {
  return value != null && value !== "" && MRP_SKU_DETAIL_TAB_SET.has(value);
}

export const MRP_SKU_DETAIL_TABS: { id: MrpSkuDetailTab; label: string }[] = [
  { id: "properties", label: "Properties" },
  { id: "bom", label: "BoM" },
  { id: "inventory", label: "Current inventory" },
  { id: "aml", label: "AML detail" },
  { id: "shopping", label: "Shopping" },
];

/**
 * Encode a SKU for the `/mrp/skus/…` path. Slashes stay as real path segments so
 * refresh and pasted URLs survive servers that decode `%2F` before React Router runs.
 */
export function encodeMrpSkuForDetailPath(sku: string): string {
  return sku.split("/").map((segment) => encodeURIComponent(segment)).join("/");
}

/** Decode the splat segment from `/mrp/skus/*` (supports legacy single-segment `%2F` bookmarks). */
export function parseMrpSkuDetailPathParam(pathParam: string): string {
  const trimmed = pathParam.trim();
  if (trimmed === "") {
    return "";
  }
  if (trimmed.includes("%2F") || trimmed.includes("%2f")) {
    try {
      return decodeURIComponent(trimmed);
    } catch {
      return trimmed;
    }
  }
  return trimmed
    .split("/")
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    })
    .join("/");
}

/**
 * Path to the full-page MRP SKU detail view (not a modal). Safe to use from
 * MRP, BoM, or ERP: always under `/mrp/skus/…`.
 */
export function buildMrpSkuDetailPath(
  sku: string,
  options?: { tab?: MrpSkuDetailTab },
): string {
  const path = `/mrp/skus/${encodeMrpSkuForDetailPath(sku)}`;
  const tab = options?.tab;
  if (tab == null || tab === "properties") {
    return path;
  }
  return `${path}?${new URLSearchParams({ tab }).toString()}`;
}

/** Incoming parts list; pass `po` to pre-fill search and show purchase-order rows only, `cpn` to filter by SKU, or `location` for an operational warehouse code. */
export function buildMrpIncomingPartsPath(options?: {
  po?: string | null;
  cpn?: string | null;
  location?: string | null;
}): string {
  const params = new URLSearchParams();
  const po = options?.po?.trim();
  const cpn = options?.cpn?.trim();
  const location = options?.location?.trim().toLowerCase();
  if (po) params.set("po", po);
  if (cpn) params.set("cpn", cpn);
  if (location) params.set("location", location);
  const qs = params.toString();
  return qs ? `/mrp/incoming-parts?${qs}` : "/mrp/incoming-parts";
}

/** Human-readable label for `public.sku.planning_mode`; null when not set. */
export function mrpSkuPlanningModeLabel(mode: MrpSkuPlanningMode | null | undefined): string | null {
  if (mode == null) {
    return null;
  }
  return MRP_SKU_PLANNING_OPTIONS.find((option) => option.value === mode)?.label ?? mode;
}

/**
 * Hub page deep link for a SKU's planning channel (forecast, safety stock, build plan, and so on).
 * Returns null when planning mode is unset or the SKU is blank.
 */
export function buildMrpPlanningModeHubPath(
  sku: string,
  planningMode: MrpSkuPlanningMode | null | undefined,
): string | null {
  const trimmed = sku.trim();
  if (trimmed === "" || planningMode == null) {
    return null;
  }

  switch (planningMode) {
    case "mrp": {
      const params = new URLSearchParams({ search: trimmed });
      return `/mrp/forecast?${params.toString()}`;
    }
    case "rop": {
      const params = new URLSearchParams({ sku: trimmed });
      return `/mrp/safety-stock-alerts?${params.toString()}`;
    }
    case "build_plan":
      return "/mrp/build-plan";
    case "supplier_forecast":
      return "/mrp/clamp-forecast";
    case "m2o":
    case "na":
      return buildMrpSkuDetailPath(trimmed);
    default:
      return buildMrpSkuDetailPath(trimmed);
  }
}

/** Full `public.sku` row from GET /api/mrp/skus/:sku (Megaventory-mirrored columns + SGS-only columns). */
export interface MrpSkuDetail {
  id: number;
  sku: string;
  serialize: boolean;
  template_id: number | null;
  manufacture: boolean;
  for_sale: boolean | null;
  advertised: boolean;
  obsolete: boolean;
  under_engineering_review: boolean;
  /** Set by quote deploy-sku when catalog flags need staff review; clear manually after review. */
  quote_deploy_needs_review: boolean;
  quote_deploy_review_reasons: string[] | null;
  /** Catalog-style part when true; false when contract-centric; null when unknown or mixed. */
  cots: boolean | null;
  mv_category_id: number | null;
  mv_description: string | null;
  mv_variant: string | null;
  mv_id: number | null;
  mv_bom_revision: string | null;
  service: boolean;
  build_to_order: boolean;
  for_sale_deployed: boolean;
  mv_product_type: string | null;
  mv_sellable: boolean | null;
  mv_purchasable: boolean | null;
  mv_unit: string;
  mv_image_url: string | null;
  mv_default_sales_price: string | null;
  mv_default_purchase_price: string | null;
  /** Megaventory ProductUnitCost (USD landed). */
  mv_unit_cost?: string | null;
  mv_supply_chain_notes: string | null;
  hub_supply_chain_notes: string | null;
  /** USD fair market value for commercial invoices when the sales order unit price is $0. */
  fair_market_value?: string | null;
  mv_hts_code: string | null;
  erp_id: string | null;
  sb_updated: string | null;
  erp_updated: string | null;
  erp_skip_import: boolean;
  /** Product title from `generate_description_parts.name` (same as generated_product_name). */
  product_name: string | null;
  bar_code_model: string | null;
  /** `mrp.reserve.reserve_quantity` keyed by MRP location code (omitted when no row). */
  mrp_reserve_by_location?: Partial<Record<MrpLocation, number>>;
  /** `mrp.reserve.replenish_to_quantity` keyed by MRP location (omitted when null in database). */
  mrp_replenish_to_by_location: Record<string, number>;
  /** Forecast order multiple from AML standard_pack_size (self row, else catalog alternates). */
  order_multiple: number;
  mrp_supplier_lead_time_days: number | null;
  mrp_planner_notes: string | null;
  mrp_planner_active: boolean;
  mrp_planner_updated_at: string | null;
  mrp_planner_updated_by: string | null;
  planning_mode: MrpSkuPlanningMode | null;
  mv_category_name: string | null;
  /** When true, mv_description and mv_variant are synced from BoM description rules. */
  template_controls_catalog_description?: boolean;
  /** Generated catalog text when template_controls_catalog_description is true. */
  generated_mv_description?: string | null;
  generated_mv_variant?: string | null;
  /** `generate_description_parts.name`. */
  generated_product_name?: string | null;
}

/** Date created column: Megaventory created time, or hub draft created time. */
export function purchaseOrderListDateCreated(
  order: Pick<MrpPurchaseOrder, "mv_created_at" | "hub_created_at" | "is_draft" | "purchase_order_mv_id">,
): string {
  const primary = order.mv_created_at?.trim();
  if (primary) {
    return formatPurchaseOrderListDate(primary);
  }
  if (order.is_draft && order.purchase_order_mv_id == null) {
    return formatPurchaseOrderListDate(order.hub_created_at ?? null);
  }
  return "—";
}

/** Hub-only draft (no Megaventory document yet). */
export function mrpPurchaseOrderIsHubOnlyDraft(
  order: Pick<MrpPurchaseOrder, "is_draft" | "purchase_order_mv_id">,
): boolean {
  return order.is_draft && order.purchase_order_mv_id == null;
}

export function mrpPurchaseOrderIsHubOwned(
  order: Pick<MrpPurchaseOrder, "system_of_record" | "is_draft" | "purchase_order_mv_id">,
): boolean {
  if (order.system_of_record === "hub") {
    return true;
  }
  return mrpPurchaseOrderIsHubOnlyDraft(order);
}

export function mrpLocationChipStyles(code: string): { label: string; className: string } {
  const normalized = code.trim().toLowerCase();
  const row = MRP_LOCATIONS.find((location) => location.code === normalized);
  const label = row?.label ?? code;
  if (normalized === "sgs") {
    return { label, className: "border-sky-200 bg-sky-50 text-sky-900" };
  }
  if (normalized === "suntronic") {
    return { label, className: "border-amber-300 bg-amber-50 text-amber-900" };
  }
  return { label, className: "border-violet-300 bg-violet-50 text-violet-900" };
}

export interface MrpComponent {
  cpn: string;
  display_name: string | null;
  qts: number | null;
  reserve_quantity: number;
  order_multiple: number;
  supplier_lead_time_days: number | null;
  notes: string | null;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface MrpAssembly {
  id: number;
  code: string;
  display_name: string;
  notes: string | null;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
  skus: { location_code: MrpLocation; sku: string }[];
}

/** Derived from assembly code for TLA vs PCBA style names (for example FI-5A-N-TLA). */
export type MrpAssemblyCodeKind = "tla" | "pcba" | "other";

export function mrpAssemblyCodeKind(code: string): MrpAssemblyCodeKind {
  const upper = code.toUpperCase();
  if (upper.includes("-PCBA")) return "pcba";
  if (upper.includes("-TLA")) return "tla";
  return "other";
}

/** When both toggles are off, no rows pass (caller may want to avoid that state in the UI). */
export function mrpAssemblyMatchesTlaPcbaFilters(
  code: string,
  showTla: boolean,
  showPcba: boolean,
): boolean {
  const kind = mrpAssemblyCodeKind(code);
  if (kind === "other") return true;
  if (kind === "tla") return showTla;
  return showPcba;
}

export function compareMrpAssembliesAlphabetically(left: MrpAssembly, right: MrpAssembly): number {
  const leftLabel = (left.display_name?.trim() || left.code).trim();
  const rightLabel = (right.display_name?.trim() || right.code).trim();
  const byLabel = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: "base" });
  if (byLabel !== 0) return byLabel;
  return left.code.localeCompare(right.code, undefined, { sensitivity: "base" });
}

export interface MrpBuildPlanRow {
  id: number;
  assembly_id: number;
  assembly_code: string;
  assembly_display_name: string;
  location_code: MrpLocation;
  plan_month: string;
  quantity: number;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface MrpGhostBuildOrderRow {
  id: number;
  sku: string;
  location_code: MrpLocation;
  plan_month: string;
  quantity: number;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface MrpGhostBuildOrderDraftLine {
  key: string;
  sku: string;
  location_code: MrpLocation;
  plan_month: string;
  quantity: string;
  notes: string;
}

export interface MrpBuildScenarioShortageRow {
  cpn: string;
  display_name: string | null;
  location_code: string;
  month: string;
  baseline_shortfall: number;
  scenario_shortfall: number;
  incremental_shortfall: number;
  scenario_order_qty: number;
  consuming_skus: { sku: string; quantity: number }[];
}

export interface MrpPurchaseOrderDeliverySegment {
  id: number;
  quantity: number;
  expected_arrival: string | null;
  sort_order: number;
}

export interface MrpPurchaseOrderLine {
  /** public.purchase_order_item.id (Megaventory line). */
  id: number;
  /** public.purchase_order.id */
  purchase_order_id: number;
  /** Megaventory purchase order document id (`public.purchase_order.mv_id`). */
  purchase_order_mv_id: number | null;
  po_number: string | null;
  supplier: string | null;
  cpn: string;
  /** Megaventory line description stored on `public.purchase_order_item.description`. */
  description: string | null;
  /** Current SKU catalog text (`mv_description` + space + `mv_variant`) for divergence checks. */
  sku_catalog_description: string | null;
  /** Megaventory `ProductUnitOfMeasurement` for the line SKU (`public.sku.mv_unit`). */
  mv_unit?: string | null;
  location_code: string;
  quantity_ordered: number;
  unit_price: number | null;
  quantity_received: number;
  /** Goods receipt (GIPI) quantity. */
  quantity_invoiced?: number;
  /** GIPI plus submitted vendor invoices that are not already a GIPI. */
  quantity_invoiced_total?: number;
  invoice_reserved?: number | null;
  available_to_invoice?: number | null;
  allocated_stock: number;
  expected_arrival: string | null;
  status: "open" | "partial" | "received" | "cancelled";
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
  /** Megaventory `PurchaseOrderRowDetailID` (`public.purchase_order_item.mv_id`). */
  mv_row_detail_id: number | null;
  /** MRP delivery splits (`mrp.purchase_order_item_delivery`); empty when the line uses header expected date only. */
  delivery_segments: MrpPurchaseOrderDeliverySegment[];
}

export type MrpPurchaseOrderEventSource = "sgs" | "megaventory_ingest";

export type MrpPurchaseOrderEventType =
  | "hub_created"
  | "lifecycle_verify"
  | "lifecycle_revert_to_pending"
  | "lifecycle_cancel"
  | "expected_date_set"
  | "expected_date_all_lines_set"
  | "delivery_split_set"
  | "hub_comment"
  | "payment_terms_set"
  | "special_instructions_set"
  | "amount_rounding_mode_set"
  | "line_description_set"
  | "line_unit_price_set"
  | "line_quantity_set"
  | "line_split"
  | "line_added"
  | "line_removed"
  | "hub_finalized"
  | "hub_approved"
  | "hub_reverted_to_pending"
  | "hub_cancelled"
  | "hub_closed"
  | "hub_receipt_submitted"
  | "hub_receipt_voided"
  | "hub_invoice_submitted"
  | "hub_gipi_created"
  | "hub_gipi_failed"
  | "hub_cutover_migrated"
  | "digikey_order_submitted";

export interface MrpPurchaseOrderEvent {
  id: number;
  purchase_order_id: number;
  purchase_order_item_id: number | null;
  source: MrpPurchaseOrderEventSource;
  event_type: MrpPurchaseOrderEventType;
  actor_user_id: string | null;
  actor_email: string | null;
  occurred_at: string;
  summary: string;
  payload: Record<string, unknown> | null;
}

export interface MrpPurchaseOrderTimeline {
  mv_created_at: string | null;
  hub_created_at: string | null;
  hub_created_by: string | null;
  hub_verified_at: string | null;
  hub_verified_by: string | null;
  hub_last_reverted_to_pending_at: string | null;
  hub_last_reverted_to_pending_by: string | null;
  hub_cancelled_at: string | null;
  hub_cancelled_by: string | null;
}

export interface MrpPurchaseOrder {
  /** public.purchase_order.id */
  id: number;
  /** Megaventory purchase order document id (`public.purchase_order.mv_id`). */
  purchase_order_mv_id: number | null;
  po_number: string | null;
  supplier: string | null;
  location_code: string;
  /** `public.purchase_order.location_id` when the header row includes it. */
  location_id?: number | null;
  /** Megaventory header status (Pending, Verified, Cancelled, …). */
  mv_status: string | null;
  /** supplier.data id for this purchase order header. */
  supplier_id: number | null;
  currency: string | null;
  /** Megaventory payment terms enumeration on the PO header. */
  payment_terms: string | null;
  /** Round each line amount, or round only the order total. */
  amount_rounding_mode?: "line" | "order" | null;
  /** Default payment terms from supplier.data for divergence checks. */
  supplier_payment_terms: string | null;
  /** Megaventory PO comments mirrored on public.purchase_order.special_instructions. */
  special_instructions: string | null;
  line_count: number;
  quantity_ordered: number;
  /** Sum of line quantity × unit price. */
  order_total: number;
  /** Null while list shell is loading received metrics. */
  quantity_received: number | null;
  /** Null while list shell is loading received metrics. */
  remaining_quantity: number | null;
  next_expected_arrival: string | null;
  /** Null while list shell is loading receiving status. */
  status: "open" | "partial" | "received" | "cancelled" | null;
  /** Megaventory document creation time from ingest (`public.purchase_order.created`). */
  mv_created_at: string;
  updated_at: string;
  cpns: string[];
  /** Per-line SKU and invoice-received quantities for list hover hints. */
  line_rows: MrpPurchaseOrderLineHoverRow[];
  /** Open MRP working draft in Megaventory (not yet placed for operations). */
  is_draft: boolean;
  draft_owner_user_id: string | null;
  draft_owner_email: string | null;
  draft_owner_display_name: string | null;
  draft_supplier_data_id: number | null;
  draft_location_code: string | null;
  /** True when the signed-in user owns this draft and may edit it on the purchase order detail page. */
  can_edit_draft: boolean;
  /** Hub draft owner (`public.purchase_order.hub_draft_owner_id`). */
  hub_draft_owner_id?: string | null;
  /** When the hub draft row was first created (`public.purchase_order.hub_created_at`). */
  hub_created_at?: string | null;
  /** hub | megaventory — Supabase authority vs legacy Megaventory mirror. */
  system_of_record?: "hub" | "megaventory" | null;
  /** Hub lifecycle when system_of_record is hub. */
  hub_status?: HubPurchaseOrderStatus | null;
  /** Derived fulfillment label from API when available. */
  fulfillment_status?: PurchaseOrderFulfillmentStatus | null;
  /** @deprecated Legacy compose list rows — use {@link mrpPurchaseOrderIsHubOnlyDraft}. */
  is_hub_compose_draft?: boolean;
  /** @deprecated Legacy compose draft slot id. */
  hub_compose_draft_id?: number | null;
}

export type HubPurchaseOrderStatus = "draft" | "ordered" | "closed" | "cancelled";

export type PurchaseOrderFulfillmentStatus =
  | "not_received"
  | "partially_received"
  | "received"
  | "partially_invoiced"
  | "invoiced";

export interface MrpPurchaseOrderInboundReceiptSummary {
  id: string;
  status: "draft" | "submitted" | "posted_to_mv";
  received_at: string | null;
  created_at: string;
  received_quantity: number;
}

export interface MrpPurchaseOrderAccountsPayableInvoiceSummary {
  id: string;
  vendor_invoice_number: string | null;
  status: string;
  gipi_sync_status: string | null;
  purchase_invoice_id: string | null;
  purchase_invoice_mv_number: string | null;
  total_amount: number | null;
  invoice_date: string | null;
  created_at: string;
}

export interface MrpPurchaseOrderDirectGipiSummary {
  id: string;
  mv_id: string | null;
  mv_number: string | null;
  mv_status: string | null;
  vendor_invoice_number: string | null;
  created_at: string | null;
}

export interface MrpPurchaseOrderDetailResponse {
  purchase_order: MrpPurchaseOrder;
  timeline: MrpPurchaseOrderTimeline;
  events: MrpPurchaseOrderEvent[];
  lines: MrpPurchaseOrderLine[];
  transfer_requests: MrpPurchaseOrderLinkedTransferRequest[];
  freshdesk_ticket_ids?: number[];
  inbound_receipts?: MrpPurchaseOrderInboundReceiptSummary[];
  accounts_payable_invoices?: MrpPurchaseOrderAccountsPayableInvoiceSummary[];
  direct_gipis?: MrpPurchaseOrderDirectGipiSummary[];
}

export interface MrpPurchaseOrderLineHoverRow {
  sku: string;
  quantity_ordered: number;
  quantity_received: number | null;
}

export type MrpPurchaseOrderReceivingStatus = Exclude<MrpPurchaseOrder["status"], null>;

/** Hover copy for Megaventory PurchaseOrderStatus on the PO header (Pending, Approved, …). */
export const MRP_PURCHASE_ORDER_MV_STATUS_HOVER_HINT =
  "Megaventory PurchaseOrderStatus on the order header (for example Pending, Approved, Cancelled). Synced from Megaventory; Verified in Megaventory is shown as Approved here. Use Approve, Revert to pending, or Cancel on this page to change it.";

export function hubPurchaseOrderStatusLabel(status: HubPurchaseOrderStatus | null | undefined): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "ordered":
      return "Ordered";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return "Unknown";
  }
}

export function hubPurchaseOrderStatusBadgeTone(
  status: HubPurchaseOrderStatus | null | undefined,
): "neutral" | "success" | "warning" | "danger" | "info" | "muted" {
  switch (status) {
    case "ordered":
      return "success";
    case "cancelled":
      return "danger";
    case "closed":
      return "muted";
    case "draft":
      return "warning";
    default:
      return "neutral";
  }
}

export function purchaseOrderFulfillmentStatusLabel(
  status: PurchaseOrderFulfillmentStatus | string | null | undefined,
): string {
  switch (status) {
    case "not_received":
      return "Not received";
    case "partially_received":
      return "Partially received";
    case "received":
      return "Received";
    case "partially_invoiced":
      return "Partially invoiced";
    case "invoiced":
      return "Invoiced";
    default:
      return "Unknown";
  }
}

export function normalizeHubPurchaseOrderStatus(
  status: string | null | undefined,
): HubPurchaseOrderStatus | null {
  const trimmed = status?.trim().toLowerCase() ?? "";
  if (trimmed === "draft") return "draft";
  if (trimmed === "ordered" || trimmed === "approved") return "ordered";
  if (trimmed === "pending") return "draft";
  if (trimmed === "closed") return "closed";
  if (trimmed === "cancelled") return "cancelled";
  return null;
}

export function purchaseOrderFulfillmentStatusBadgeTone(
  status: PurchaseOrderFulfillmentStatus | string | null | undefined,
): "neutral" | "success" | "warning" | "danger" | "info" | "muted" {
  switch (status) {
    case "invoiced":
      return "success";
    case "partially_invoiced":
      return "info";
    case "received":
      return "success";
    case "partially_received":
      return "warning";
    case "not_received":
      return "muted";
    default:
      return "neutral";
  }
}

export const MRP_PURCHASE_ORDER_HUB_LIFECYCLE_HOVER_HINT =
  "Hub purchase order lifecycle (draft, ordered, closed, cancelled). Order and cancel actions live on the purchase order detail page.";

export const MRP_PURCHASE_ORDER_FULFILLMENT_HOVER_HINT =
  "Received is warehouse Goods IN plus parentless GIPI quantity (combo receive and invoice). Invoiced is every GIPI on the line, including vendor-invoice GIPIs.";

/** Operator-facing label for Megaventory purchase order header status. */
export function mrpPurchaseOrderMvStatusLabel(mvStatus: string | null | undefined): string {
  const trimmed = mvStatus?.trim() ?? "";
  if (trimmed === "") return "—";
  const normalized = trimmed.toLowerCase();
  if (normalized === "verified") return "Approved";
  if (normalized === "pending") return "Pending";
  if (normalized === "cancelled" || normalized === "canceled") return "Cancelled";
  if (normalized === "closed") return "Closed";
  if (normalized === "fullyinvoiced") return "Fully invoiced";
  return trimmed;
}

/** Hover copy for hub receiving progress derived from linked purchase invoice (GIPI) quantities. */
export const MRP_PURCHASE_ORDER_RECEIVING_STATUS_HOVER_HINT =
  "Hub receiving progress from warehouse receipts plus parentless GIPI quantity. This is not Megaventory PO status.";

export const MRP_PURCHASE_ORDER_RECEIVED_QTY_HOVER_HINT =
  "Warehouse Goods IN plus parentless GIPI quantity. A parentless GIPI is a combo receive and invoice, so it counts here even when there is no inbound receipt row.";

export const MRP_PURCHASE_ORDER_INVOICED_QTY_HOVER_HINT =
  "Submitted vendor invoice quantity on this line. Goods receipts are the GIPI column and are not added here.";

export const MRP_PURCHASE_ORDER_GIPI_QTY_HOVER_HINT =
  "Goods receipt quantity on this line: parentless Create GIPI documents and GIPIs created from a vendor invoice.";

/** Operator-facing label for hub receiving progress (not Megaventory PO status). */
export function mrpPurchaseOrderReceivingStatusLabel(
  status: MrpPurchaseOrderReceivingStatus,
): string {
  switch (status) {
    case "open":
      return "Not on invoices";
    case "partial":
      return "Partially on invoices";
    case "received":
      return "Fully on invoices";
    case "cancelled":
      return "Cancelled";
    default:
      return status ?? "…";
  }
}

/** Operator-facing label for who owns an open MRP purchase order draft. */
export function mrpPurchaseOrderDraftOwnerLabel(order: Pick<
  MrpPurchaseOrder,
  "draft_owner_display_name" | "draft_owner_email"
>): string {
  if (order.draft_owner_display_name) {
    return order.draft_owner_display_name;
  }
  const email = order.draft_owner_email?.trim();
  if (email) {
    const localPart = email.split("@")[0]?.trim();
    return localPart && localPart.length > 0 ? localPart : email;
  }
  return "Unknown";
}

/** @deprecated Use `/mrp/purchase-orders/{id}` for hub-only drafts. */
export function mrpHubComposeDraftListHref(
  order: Pick<MrpPurchaseOrder, "id">,
): string {
  return `/mrp/purchase-orders/${order.id}`;
}

/** Transfer request created from a purchase-order line on create/draft/finalize. */
export interface MrpPurchaseOrderLinkedTransferRequest {
  id: string;
  sku_id: string;
  source_public_purchase_order_item_id?: number | null;
  origin_location_id: number;
  destination_location_id: number;
  target_date: string;
  requested_quantity: number;
  remaining_quantity: number;
  status: MrpTransferRequestStatus;
  reason: string;
  notes: string | null;
  created_by: string;
  created_at: string;
}

/**
 * Status of a public.transfer_order. The same enum is used in the database; this
 * mirror lets the UI render badges and decide which legs are visible without
 * importing server types.
 */
export type MrpTransferOrderStatus =
  | "pending"
  | "packing"
  | "ready_to_ship"
  | "shipped"
  | "delivered"
  | "partially_received"
  | "received"
  | "cancelled";

/** `mrp.transfer_request.status` — planner intent before a pending transfer order exists. */
export type MrpTransferRequestStatus = "open" | "partially_allocated" | "fulfilled" | "cancelled";

/**
 * Active transfer reasons (`public.transfer_reason_catalog`). Legacy enum values are
 * normalized on read via {@link normalizeMrpTransferReason}.
 */
export const ACTIVE_MRP_TRANSFER_REASONS = [
  "upcoming_needs",
  "shortage_response",
  "equipment_tooling",
  "prototype_qc",
  "other",
] as const;

export type MrpTransferReason = (typeof ACTIVE_MRP_TRANSFER_REASONS)[number];

/** @deprecated Alias for {@link ACTIVE_MRP_TRANSFER_REASONS}. */
export const MRP_TRANSFER_REASONS = ACTIVE_MRP_TRANSFER_REASONS;

export const LEGACY_MRP_TRANSFER_REASON_MAP: Readonly<Record<string, MrpTransferReason>> = {
  kitting: "upcoming_needs",
  restock: "upcoming_needs",
  obsolete_replacement: "upcoming_needs",
  workstation_setup: "equipment_tooling",
  testing_samples: "prototype_qc",
};

export const DEFAULT_MRP_TRANSFER_REASON: MrpTransferReason = "upcoming_needs";

export interface MrpTransferReasonCatalogEntry {
  code: MrpTransferReason;
  label: string;
  description: string;
}

// Accepts null/undefined: transfer_order_item.reason is nullable and rows
// predating the reason column carry NULL — they normalize to the default.
export function normalizeMrpTransferReason(reason: string | null | undefined): MrpTransferReason {
  const trimmed = (reason ?? "").trim();
  if (trimmed.length === 0) return DEFAULT_MRP_TRANSFER_REASON;
  const merged = LEGACY_MRP_TRANSFER_REASON_MAP[trimmed];
  if (merged != null) return merged;
  if ((ACTIVE_MRP_TRANSFER_REASONS as readonly string[]).includes(trimmed)) {
    return trimmed as MrpTransferReason;
  }
  return DEFAULT_MRP_TRANSFER_REASON;
}

export async function fetchMrpTransferReasonCatalog(): Promise<MrpTransferReasonCatalogEntry[]> {
  const response = await apiFetch<{ reasons: MrpTransferReasonCatalogEntry[] }>(
    "/api/mrp/transfer-reasons",
  );
  return response.reasons;
}

/** Operator-facing label for a transfer reason code. */
export function mrpTransferReasonLabel(
  reason: MrpTransferReason | string,
  catalog?: readonly MrpTransferReasonCatalogEntry[],
): string {
  const code = normalizeMrpTransferReason(reason);
  const entry = catalog?.find((row) => row.code === code);
  return entry?.label ?? code.replaceAll("_", " ");
}

/**
 * GET /api/mrp/incoming response. `sku_hover_hints` is `{}` when the client passes `include_sku_hints=0`
 * (for example kitting lists that do not need description hovers).
 */
export interface MrpIncomingFeedResponse {
  rows: MrpIncomingRow[];
  sku_hover_hints: Record<string, MrpSkuHoverHintFields>;
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
}

/** Cache key segment for hub-cache SSE (`mrp.incoming:<key>`); mirrors server `mrpIncomingCacheKey`. */
export function mrpIncomingCacheKey(options: {
  locations: string;
  cpn?: string | null;
  includeSkuHints?: boolean;
  workOrderIncomingMode?: string;
}): string {
  const cpn = options.cpn?.trim().toLowerCase() ?? "";
  const includeSkuHints = options.includeSkuHints !== false ? "1" : "0";
  const workOrderIncomingMode = options.workOrderIncomingMode?.trim().toLowerCase() ?? "";
  return `${options.locations}|${cpn}|${includeSkuHints}|${workOrderIncomingMode}`;
}

/**
 * Row shape returned by GET /api/mrp/incoming. Discriminated by `kind`:
 *   - kind="po": public.purchase_order_item (Megaventory) with remaining qty; `id` is that row id
 *   - kind="non_po": mrp.incoming_part; `id` is incoming_part id
 *   - kind="work_order": default mode uses `public.build_order` (finished good SKU, remaining ordered minus received).
 *     `id` is `public.build_order.id`. With `work_order_incoming_mode=components`, rows are `public.build_order_bom`
 *     lines and `id` is that BOM row id. `batch_ids` lists distinct `public.batch.id` via `public.batch_item`.
 *     `expected_arrival` prefers batch manufacturer ship-by, then hub override, then MV-derived date (min BOM line
 *     expected consume date for finished-good mode, or the line date for components mode).
 *     Finished-good rows set `quantity_ordered` / `quantity_received` from `public.build_order` so Rem / total matches PO style.
 *   - kind="transfer_order": public.transfer_order_item, one leg per row (`transfer_role`);
 *     `id` is the transfer_order_item id (the same id appears on the source and the destination
 *     leg, so the UI can group them by `transfer_group_id` when both ends are visible).
 *   - kind="transfer_request": mrp.transfer_request (open planner intent). `id` is the request id;
 *     `transfer_group_id` matches `id` for pairing; `po_number` is `REQ-<id>`; `expected_arrival`
 *     is the target date. Shown under the Transfer kind filter until converted to a transfer order.
 */
export interface MrpIncomingRow {
  kind: "po" | "non_po" | "work_order" | "transfer_order" | "transfer_request";
  id: number;
  cpn: string;
  location_code: string;
  quantity_remaining: number;
  quantity_ordered: number | null;
  quantity_received: number | null;
  allocated_stock: number | null;
  expected_arrival: string | null;
  po_number: string | null;
  supplier: string | null;
  status: "open" | "partial" | "received" | "cancelled" | null;
  source: string | null;
  notes: string | null;
  /** Carrier or shipment tracking reference; editable on all incoming row kinds. */
  tracking_number: string | null;
  /** Inbound carrier on manual (non-PO) rows; used when merging into Shipping Arrivals. */
  tracking_carrier: string | null;
  updated_at: string;
  updated_by: string | null;
  /** Present on kind=po (Megaventory). */
  po_data_source: "megaventory" | null;
  /** Link to public.purchase_order_item when a spreadsheet line matched this MV row. */
  matched_public_purchase_order_item_id: number | null;
  /**
   * When kind=po and splits exist in mrp.purchase_order_item_delivery, one incoming row per segment.
   * Null when using a single expected date on public.purchase_order_item.expected_date.
   */
  delivery_schedule_id: number | null;
  delivery_sort_order: number | null;
  /** Transfer fields (null unless kind is transfer_order or transfer_request). */
  transfer_group_id: number | null;
  transfer_role: "from" | "to" | null;
  transfer_order_id: number | null;
  transfer_number: string | null;
  transfer_status: MrpTransferOrderStatus | MrpTransferRequestStatus | null;
  /** Operational warehouse code on the from / to ends. */
  from_location_code: string | null;
  to_location_code: string | null;
  /** Display label (`public.location.abbreviation` falling back to name) for the from / to ends. */
  from_location_label: string | null;
  to_location_label: string | null;
  /** Token used to deep-link out to the transfer-order app for non-pending statuses. */
  transfer_access_token: string | null;
  /**
   * Manufacturing batch ids from `public.batch_item` for this work order (`kind="work_order"` only);
   * empty array for other row kinds.
   */
  batch_ids: number[];
  /** `public.build_order.id` when `kind="work_order"` (including components mode, where `id` is the BOM line). */
  work_order_id: number | null;
  /** Megaventory build order number (`public.build_order.mv_number`) when `kind="work_order"`. */
  work_order_mv_number: string | null;
  /** Megaventory document id for `public.build_order.mv_id` when `kind="work_order"`; null otherwise. */
  work_order_mv_id: number | null;
  /**
   * Megaventory work order header `mv_status` when `kind="work_order"`; null for other row kinds and when unset.
   */
  work_order_mv_status: string | null;
  /** `public.purchase_order.id` when `kind="po"`; null for other row kinds. */
  purchase_order_id: number | null;
  /**
   * Megaventory `public.purchase_order.mv_id` when `kind="po"`; null for other kinds and non-Megaventory headers.
   */
  purchase_order_mv_id: number | null;
  /** `public.purchase_order.system_of_record` when `kind="po"`; null for other row kinds. */
  system_of_record: "hub" | "megaventory" | string | null;
  /**
   * Purchase order header status when `kind="po"`: hub lifecycle for Hub-owned rows,
   * otherwise Megaventory status. Null when unset (UI may fall back to line status).
   */
  purchase_order_mv_status: string | null;
  /**
   * When `kind="work_order"`, earliest `public.batch.mfr_ship_by_date` among batches linked through
   * `public.batch_item` for this build order; null when none. If set, incoming expected date follows this value and is not hub-editable.
   */
  work_order_batch_mfr_ship_by: string | null;
  /**
   * BOM kit destination (`bom.template_initial.kit_to_station_id`) for `kind="work_order"` component
   * demand; null when unset or not resolved from the finished-good template.
   */
  kit_to_station_id: number | null;
  /** `public.station.name` for {@link kit_to_station_id}; null when id is unset. */
  kit_to_station_name: string | null;
}

export interface MrpIncomingPart {
  id: number;
  cpn: string;
  location_code: MrpLocation;
  quantity: number;
  expected_arrival: string | null;
  source: string | null;
  notes: string | null;
  tracking_number: string | null;
  updated_at: string;
  updated_by: string | null;
}

/**
 * GET /api/mrp/inventory — latest `mrp.inventory_snapshot` per (cpn, location).
 * `quantity` is Megaventory planning on-hand (StockPhysical minus open SO and WO allocation).
 * `stock_physical` is **StockPhysical** (actual physical). Megaventory **StockOnHand** is a separate API field
 * (forecasted balance after all orders complete; confirmed by Eric via the MV UI) and is **not** stored here.
 * Breakout fields come from the same stock row; `stock_physical` should equal
 * `quantity + outgoing_sales_orders + outgoing_work_orders` when integers match MV.
 * Envelope also includes `inventory_last_full_sync_at` (UTC ISO from last successful full live inventory ingest).
 */
export interface MrpInventoryRow {
  cpn: string;
  /** Supabase `public.location.id` for this Megaventory inventory location mirror. */
  location_id?: number;
  /** `public.location.mrp_site_code` or abbreviation — display key for this row. */
  location_code: string;
  /** Human-readable site name from `public.location.name`. */
  location_name?: string | null;
  location_abbreviation?: string | null;
  /** True when this row is an MRP planning site (sgs / suntronic / kodiak). */
  is_mrp_planning_site?: boolean;
  /**
   * Civil snapshot day from ingest (America/Chicago date). Not a sync clock — use
   * `MrpInventoryListResponse.inventory_last_full_sync_at` for “As of” timestamps.
   * Null when the site was padded because no `mrp.inventory_snapshot` row exists (all amounts still zero).
   */
  snapshot_date: string | null;
  /** MRP planning quantity (StockPhysical − outgoing sales − outgoing WO). */
  quantity: number;
  /** This site's share of SKU reserve when `cpn` is filtered on GET /inventory. */
  reserve_allocated?: number;
  /** `stock_physical` minus outgoing sales, hub/legacy unallocated WO demand, and reserve (`cpn` query). */
  planning_on_hand?: number;
  /** StockNonReceivedPOs. */
  incoming_purchase_orders: number;
  /** StockNonReceivedWOs. */
  incoming_work_orders: number;
  /**
   * Remaining finished-good quantity on open hub-native work orders for this CPN
   * (`cpn` or `cpns` query). Not included in Megaventory `incoming_work_orders`.
   */
  hub_incoming_work_orders?: number;
  /** StockNonShipped. */
  outgoing_sales_orders: number;
  /** StockNonAllocatedWOs. */
  outgoing_work_orders: number;
  /** Open work-order BOM lines for this site when GET /inventory is filtered by `cpn`. */
  outgoing_work_order_lines?: MrpInventoryOutgoingWorkOrderLine[];
  /** Open hub-native finished-good receipt lines for this CPN (`cpn` query). */
  incoming_work_order_lines?: MrpInventoryOutgoingWorkOrderLine[];
  /** Soft-allocated quantity on open hub-native work orders (`cpn` or `cpns` query). Megaventory WIP is already out of physical. */
  hub_soft_allocated_wip?: number;
  /**
   * Unallocated BOM demand on open hub-native and legacy work orders (`cpn` or `cpns` query).
   * Equals open WO demand minus soft-allocated WIP for those BOM rows.
   */
  hub_unallocated_demand?: number;
  /** `stock_physical` minus hub-native `hub_soft_allocated_wip` (`cpn` or `cpns` query). */
  stock_physical_available?: number;
  /** Hub sublocation balances for this CPN at this site (`cpn` query only). */
  sublocation_quantities?: MrpInventorySublocationQuantity[];
  /** Live UUID inventory containers at this site (`cpn` query only). */
  sublocation_containers?: MrpInventoryContainerSublocation[];
  /** Megaventory StockPhysical — actual physical (sum at the site for this CPN). */
  stock_physical: number;
  /** Megaventory `SubLocation` on the stock row, meaningful for MRP site `sgs`; null for other sites and padded zero rows. */
  sublocation: string | null;
  source: string | null;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** Short hover hint for inventory snapshot Available (SKU detail and allocate modal). */
export const MRP_INVENTORY_STOCK_PHYSICAL_AVAILABLE_SHORT_HINT =
  "Free shelf stock at this site. Hover for Physical minus WIP breakdown.";

/**
 * Free shelf stock at a site for a CPN — same as the SKU detail inventory Available column.
 * Prefer API `stock_physical_available` when present; otherwise physical minus hub-native soft WIP.
 */
export function mrpInventoryStockPhysicalAvailable(row: MrpInventoryRow): number {
  const physical = Number(row.stock_physical) || 0;
  const wip = row.hub_soft_allocated_wip ?? 0;
  if (row.stock_physical_available != null && Number.isFinite(Number(row.stock_physical_available))) {
    return Math.trunc(Number(row.stock_physical_available));
  }
  return Math.trunc(physical - wip);
}

/**
 * Outgoing on work orders for inventory snapshot columns.
 * Prefer hub/legacy unallocated BOM demand (open WO demand minus soft-allocated WIP).
 * Also keep any Megaventory StockNonAllocatedWOs residual after WIP, in case MV
 * still shows demand not mirrored on hub BOM rows.
 */
export function mrpInventoryOutgoingWorkOrders(row: MrpInventoryRow): number {
  const wip = Math.max(0, Math.trunc(Number(row.hub_soft_allocated_wip) || 0));
  const mvOutgoing = Math.max(0, Math.trunc(Number(row.outgoing_work_orders) || 0));
  const mvResidual = Math.max(0, mvOutgoing - wip);
  if (row.hub_unallocated_demand != null && Number.isFinite(Number(row.hub_unallocated_demand))) {
    const hubUnallocated = Math.max(0, Math.trunc(Number(row.hub_unallocated_demand) || 0));
    return Math.max(hubUnallocated, mvResidual);
  }
  return mvResidual;
}

/**
 * Incoming on work orders for inventory snapshot columns.
 * Megaventory StockNonReceivedWOs plus remaining finished goods on open hub-native WOs.
 */
export function mrpInventoryIncomingWorkOrders(row: MrpInventoryRow): number {
  const mvIncoming = Math.max(0, Math.trunc(Number(row.incoming_work_orders) || 0));
  const hubIncoming = Math.max(0, Math.trunc(Number(row.hub_incoming_work_orders) || 0));
  return mvIncoming + hubIncoming;
}

/** Stock-backed BOM lines use on-hand inventory when soft-allocating (not Megaventory service SKUs). */
export function mrpInventoryBomLineBindsShelfAllocateCap(mvProductType?: string | null): boolean {
  return (mvProductType ?? "").trim().toLowerCase() !== "service";
}

/**
 * Max increment for one soft-allocate request on a BOM line after other work orders' WIP.
 * Matches hub-native `physical - otherSoftWip` when the row came from GET /inventory?cpn=….
 */
export function mrpInventorySoftAllocateIncrementCap(
  row: MrpInventoryRow | undefined,
  quantityAlreadyAllocatedOnWorkOrderLine: number,
): number | null {
  if (row == null) {
    return null;
  }
  const alreadyAllocated = Math.max(0, Math.trunc(quantityAlreadyAllocatedOnWorkOrderLine));
  return Math.max(0, mrpInventoryStockPhysicalAvailable(row) + alreadyAllocated);
}

/** Max allocate-now quantity for one work order BOM line (BOM remaining and shelf cap). */
export function mrpWorkOrderMaxAllocateQuantity(
  bomLine: {
    quantityRemaining: number;
    quantityAllocated: number;
    mvProductType?: string | null;
  },
  inventoryRow: MrpInventoryRow | undefined,
): number {
  const remaining = Math.max(0, Math.trunc(bomLine.quantityRemaining));
  if (!mrpInventoryBomLineBindsShelfAllocateCap(bomLine.mvProductType)) {
    return remaining;
  }
  const shelfCap = mrpInventorySoftAllocateIncrementCap(inventoryRow, bomLine.quantityAllocated);
  if (shelfCap == null) {
    return remaining;
  }
  return Math.min(remaining, shelfCap);
}

/**
 * Live / submit validation for one Allocate now field.
 * Returns an error when the value exceeds remaining demand or (for stock SKUs) available shelf.
 * Service SKUs are not capped by available.
 */
export function allocateNowQuantityValidationIssue(input: {
  componentSku: string;
  rawQuantity: string;
  quantityRemaining: number;
  quantityAllocated: number;
  mvProductType?: string | null;
  inventoryRow?: MrpInventoryRow;
}): string | null {
  const sku = input.componentSku.trim() || "component";
  const trimmed = input.rawQuantity.trim();
  if (trimmed === "") {
    return null;
  }
  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric)) {
    return `Invalid quantity for ${sku}: use a whole number, 0, or leave blank to skip.`;
  }
  if (numeric < 0) {
    return `Quantity for ${sku} cannot be negative.`;
  }
  if (!Number.isInteger(numeric)) {
    return `Quantity for ${sku} must be a whole number.`;
  }
  if (numeric === 0) {
    return null;
  }

  const remaining = Math.max(0, Math.trunc(input.quantityRemaining));
  if (numeric > remaining) {
    return `Allocate now for ${sku} cannot exceed remaining to allocate (${remaining}).`;
  }

  if (!mrpInventoryBomLineBindsShelfAllocateCap(input.mvProductType)) {
    return null;
  }

  const shelfCap = mrpInventorySoftAllocateIncrementCap(
    input.inventoryRow,
    input.quantityAllocated,
  );
  if (shelfCap == null) {
    return null;
  }
  if (numeric > shelfCap) {
    const available =
      input.inventoryRow != null
        ? mrpInventoryStockPhysicalAvailable(input.inventoryRow)
        : shelfCap;
    return `Allocate now for ${sku} cannot exceed available (${available}).`;
  }
  return null;
}

/** Envelope for GET /api/mrp/inventory. */
export interface MrpInventoryListResponse {
  rows: MrpInventoryRow[];
  /** UTC ISO instant of the last successful full live inventory ingest; null if never recorded. */
  inventory_last_full_sync_at: string | null;
}

/**
 * Per-SKU verified-at: newer of last full inventory ingest and that SKU's latest row `updated_at`.
 * Full sync verifies every SKU even when unchanged rows keep an older `updated_at`.
 * Per-SKU Megaventory refresh always touches `updated_at`, so that counts as a newer verify.
 */
export function mrpInventorySkuVerifiedAtMs(
  rowUpdatedAt: string | null | undefined,
  inventoryLastFullSyncAt: string | null | undefined,
): number | null {
  let best = Number.NEGATIVE_INFINITY;
  for (const raw of [rowUpdatedAt, inventoryLastFullSyncAt]) {
    if (raw == null || String(raw).trim() === "") continue;
    const ms = Date.parse(String(raw));
    if (Number.isFinite(ms) && ms > best) best = ms;
  }
  return best === Number.NEGATIVE_INFINITY ? null : best;
}

/**
 * Multi-SKU inventory freshness for allocate / BOM views: the oldest per-SKU verified-at.
 * Example: full sync 20 minutes ago, one SKU individually refreshed 2 minutes ago → 20 minutes ago.
 */
export function mrpInventorySetVerifiedAsOf(
  componentSkus: readonly string[],
  rows: readonly Pick<MrpInventoryRow, "cpn" | "updated_at">[],
  inventoryLastFullSyncAt: string | null | undefined,
): Date | null {
  const maxUpdatedByCpn = new Map<string, number>();
  for (const row of rows) {
    const key = String(row.cpn ?? "").trim().toLowerCase();
    if (key.length === 0) continue;
    if (row.updated_at == null || String(row.updated_at).trim() === "") continue;
    const ms = Date.parse(String(row.updated_at));
    if (!Number.isFinite(ms)) continue;
    const previous = maxUpdatedByCpn.get(key);
    if (previous == null || ms > previous) maxUpdatedByCpn.set(key, ms);
  }

  const uniqueSkus = [
    ...new Set(componentSkus.map((sku) => sku.trim().toLowerCase()).filter((sku) => sku.length > 0)),
  ];
  if (uniqueSkus.length === 0) {
    const fullOnly = mrpInventorySkuVerifiedAtMs(null, inventoryLastFullSyncAt);
    return fullOnly == null ? null : new Date(fullOnly);
  }

  let oldest = Number.POSITIVE_INFINITY;
  for (const sku of uniqueSkus) {
    const rowMs = maxUpdatedByCpn.get(sku);
    const verified = mrpInventorySkuVerifiedAtMs(
      rowMs != null ? new Date(rowMs).toISOString() : null,
      inventoryLastFullSyncAt,
    );
    if (verified == null) continue;
    if (verified < oldest) oldest = verified;
  }
  return oldest === Number.POSITIVE_INFINITY ? null : new Date(oldest);
}

/** Short relative age for inventory verified-at labels (ASCII). */
export function formatMrpInventoryVerifiedAge(asOf: Date, nowMs: number = Date.now()): string {
  const deltaMs = nowMs - asOf.getTime();
  if (!Number.isFinite(deltaMs)) return asOf.toLocaleString();
  if (deltaMs < 15_000) return "just now";
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 minute ago";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours === 1) return "1 hour ago";
  if (hours < 48) return `${hours} hours ago`;
  return asOf.toLocaleString();
}

/** Default max age before SKU inventory tab re-pulls Megaventory for that CPN. */
export const MRP_INVENTORY_SKU_REFRESH_MAX_AGE_MS = 10_000;

export interface MrpInventorySkuRefreshResponse {
  refreshed: boolean;
  cpn: string;
  inventory_last_full_sync_at: string | null;
  sku_synced_at: string | null;
  reason?: "fresh";
  locations_upserted?: number;
  locations_unchanged?: number;
  locations_removed?: number;
  megaventory_products?: number;
  max_age_ms?: number;
}

export interface MrpInventorySkusRefreshResponse {
  refreshed: boolean;
  cpns: string[];
  skipped_fresh: string[];
  inventory_last_full_sync_at: string | null;
  sku_synced_at: string | null;
  reason?: "fresh";
  locations_upserted?: number;
  locations_unchanged?: number;
  locations_removed?: number;
  megaventory_products?: number;
  max_age_ms?: number;
}

export interface MrpInventoryFullRefreshAccepted {
  accepted: true;
  run_id: number;
}

export interface MrpInventoryFullRefreshRunStatus {
  run_id: number;
  status: string;
  ended_at: string | null;
}

/** Shared busy flag for Forecast Resync inventory (any viewer). */
export interface MrpInventoryFullRefreshActive {
  running: boolean;
  run_id: number | null;
  status: string | null;
  started_at: string | null;
  triggered_by: string | null;
}

/** GET /api/mrp/inventory-sublocations — hub qty per floor sublocation (not synced per-bucket to MV). */
export interface MrpInventorySublocationRow {
  location_id: number;
  location_code: string;
  cpn: string;
  /** Empty string means "No sub location". */
  sublocation: string;
  quantity: number;
  updated_at: string;
  updated_by: string | null;
}

export interface MrpInventoryOutgoingWorkOrderLine {
  work_order_no: string;
  quantity: number;
  /** Finished-good SKU on the open work order consuming this component. */
  finished_good_sku: string;
  /** Hub-native line kind when present (`unallocated` vs `soft_allocated`). */
  demand_kind?: "unallocated" | "soft_allocated" | "legacy_mv" | "hub_incoming";
  build_order_id?: number | null;
  /** CM purchase order (GPO) or related sales order (GSO) on the work order. */
  reference?: string | null;
  purchase_order_id?: number | null;
  sales_order_id?: number | null;
  /** Work order header ship-by, else earliest related batch ship-by. */
  work_order_promise_date?: string | null;
  /** Linked sales order `sgs_ship_by_date` when the reference is a GSO. */
  sales_order_ship_date?: string | null;
  /** Earliest purchase-order line expected date when the reference is a GPO. */
  purchase_order_promise_date?: string | null;
  sales_order_client_name?: string | null;
  sales_order_end_user_name?: string | null;
  sales_order_customer_po?: string | null;
}

export interface MrpInventorySublocationQuantity {
  sublocation: string;
  quantity: number;
}

export interface MrpInventoryContainerSublocation {
  id: string;
  short_code: string;
  quantity: number;
  sublocation: string;
}

export interface MrpForecastCell {
  month: string;
  starting_qty: number;
  arrivals: number;
  demand: number;
  ending_qty: number;
  reserve_target: number;
  shortfall: number;
  order_qty: number;
  /** Build demand attributed to each finished-good physical SKU (from forecast engine). */
  demand_by_consuming_sku?: { sku: string; quantity: number }[];
  /** Incoming supply split by source stream for hover hints. */
  arrivals_by_kind?: { kind: MrpForecastArrivalKind; quantity: number }[];
}

export type MrpForecastArrivalKind = "ghost" | "manual" | "transfer" | "po" | "work_order";

export const MRP_FORECAST_ARRIVAL_KIND_ORDER: MrpForecastArrivalKind[] = [
  "ghost",
  "manual",
  "transfer",
  "po",
  "work_order",
];

export const MRP_FORECAST_ARRIVAL_KIND_LABEL: Record<MrpForecastArrivalKind, string> = {
  ghost: "Ghost",
  manual: "Manual arrival",
  transfer: "Transfer",
  po: "PO",
  work_order: "Work order",
};

export interface MrpForecastSeries {
  cpn: string;
  display_name: string | null;
  location_code: string;
  reserve_quantity: number;
  allocated_reserve: number;
  order_multiple: number;
  cells: MrpForecastCell[];
  /** From public.sku when present; null if CPN is not in parts master. */
  planning_mode: MrpSkuPlanningMode | null;
}

export type MrpForecastSkippedReason = "missing_assembly_sku" | "bom_explosion_failed";

export interface MrpForecastSkippedItem {
  reason: MrpForecastSkippedReason;
  assembly_code?: string;
  location_code?: string;
  finished_good_sku?: string;
  message: string;
}

export interface MrpSkuHoverHintFields {
  category: string | null;
  description: string | null;
  variant: string | null;
  /** Megaventory `ProductUnitOfMeasurement` mirror (`public.sku.mv_unit`). */
  mv_unit?: string | null;
  /** Hub extended notes, else Megaventory mirror. */
  supply_chain_notes: string | null;
  /** Standard pack size from AML. */
  order_multiple?: number;
}

export function mrpSkuHasSupplyChainNotes(hint: MrpSkuHoverHintFields | undefined): boolean {
  return (hint?.supply_chain_notes?.trim() ?? "") !== "";
}

export function isMrpIncomingTransferKind(
  kind: MrpIncomingRow["kind"],
): kind is "transfer_order" | "transfer_request" {
  return kind === "transfer_order" || kind === "transfer_request";
}

/** Tailwind classes for transfer-order execution status badges. */
export function mrpTransferStatusBadgeStyles(
  status: MrpTransferOrderStatus,
): { label: string; className: string } {
  if (status === "pending") {
    return { label: status, className: "border-slate-300 bg-slate-50 text-slate-700" };
  }
  if (status === "packing" || status === "ready_to_ship") {
    return { label: status, className: "border-amber-300 bg-amber-50 text-amber-900" };
  }
  if (status === "shipped" || status === "delivered" || status === "partially_received") {
    return { label: status, className: "border-sky-300 bg-sky-50 text-sky-900" };
  }
  if (status === "received") {
    return { label: status, className: "border-emerald-300 bg-emerald-50 text-emerald-900" };
  }
  return { label: status, className: "border-rose-300 bg-rose-50 text-rose-900" };
}

/** Badge for open planner transfer requests on Incoming parts. */
export function mrpTransferRequestStatusBadgeStyles(
  status: MrpTransferRequestStatus,
): { label: string; className: string } {
  if (status === "open") {
    return { label: "Request open", className: "border-violet-300 bg-violet-50 text-violet-900" };
  }
  if (status === "partially_allocated") {
    return {
      label: "Partially allocated",
      className: "border-amber-300 bg-amber-50 text-amber-900",
    };
  }
  if (status === "fulfilled") {
    return { label: "Fulfilled", className: "border-emerald-300 bg-emerald-50 text-emerald-900" };
  }
  return { label: status, className: "border-rose-300 bg-rose-50 text-rose-900" };
}

/** Only `pending` transfer orders can be edited or deleted from the Incoming Parts UI. */
export function isMrpTransferOrderEditable(
  status: MrpTransferOrderStatus | MrpTransferRequestStatus | null,
): boolean {
  return status === "pending";
}

/** Open transfer requests allow target-date edits from Incoming parts. */
export function isMrpTransferRequestEditable(
  status: MrpTransferRequestStatus | MrpTransferOrderStatus | null,
): boolean {
  return status === "open";
}

/**
 * Open or remaining-after-partial requests can be cancelled. That closes
 * placeholder demand only; existing transfer orders stay as they are.
 */
export function isMrpTransferRequestCancellable(
  status: MrpTransferRequestStatus | MrpTransferOrderStatus | null,
): boolean {
  return status === "open" || status === "partially_allocated";
}

export const MRP_TRANSFER_REQUEST_CANCEL_REMAINDER_HOVER =
  "Closes remaining demand. Existing transfer orders stay as they are.";

export function mrpTransferRequestCancelButtonLabel(hasAllocation: boolean): string {
  return hasAllocation ? "Cancel remainder" : "Cancel";
}

/** Fixed width so Cancel and Cancel remainder share one column width in transfer tables. */
export const MRP_TRANSFER_REQUEST_CANCEL_BUTTON_CLASS =
  "w-[9.5rem] shrink-0 whitespace-nowrap";

export function mrpTransferRequestCancelConfirmMessage(input: {
  sku: string;
  remainingQuantity: number;
  hasAllocation: boolean;
}): string {
  if (input.hasAllocation) {
    return `Close remaining ${input.remainingQuantity} of ${input.sku}? Existing transfer orders stay as they are.`;
  }
  return `Cancel transfer request for ${input.sku}?`;
}

/** Native `title` text for hovering a CPN / SKU in MRP tables. */
/** Megaventory default purchase order line description from SKU catalog fields. */
export function buildMrpSkuCatalogLineDescription(hint: MrpSkuHoverHintFields | undefined): string | null {
  if (hint == null) return null;
  const parts = [hint.description?.trim(), hint.variant?.trim()].filter((part) => (part ?? "").length > 0);
  return parts.length > 0 ? parts.join(" ") : null;
}

export function mrpPurchaseOrderLineDescriptionDiffersFromSku(
  lineDescription: string | null | undefined,
  skuCatalogDescription: string | null | undefined,
): boolean {
  const catalog = (skuCatalogDescription ?? "").trim();
  if (catalog === "") return false;
  return (lineDescription ?? "").trim() !== catalog;
}

export function formatMrpSkuHoverHintTitle(sku: string, hint: MrpSkuHoverHintFields | undefined): string {
  const category = hint?.category?.trim() || "—";
  const description = hint?.description?.trim() || "—";
  const variant = hint?.variant?.trim() || "—";
  return `${sku} — Category: ${category}; Description: ${description}; Variant: ${variant}`;
}

export const MRP_FORECAST_PLACEHOLDER_ITEM_SOURCE = "mrp_forecast_placeholder";

export interface MrpForecastResult {
  months: string[];
  series: MrpForecastSeries[];
  /** YYYY-MM-DD per site; build-plan demand is prorated in months where this lands (see MRP settings). */
  next_component_order_date_by_location?: Record<string, string | null>;
  /** Build plan rows or finished-good SKUs omitted so the forecast could complete. */
  skipped_items?: MrpForecastSkippedItem[];
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
}

/** Cache key segment for hub-cache SSE (`mrp.clamp_forecast:<key>`); mirrors server `clampForecastCacheKey`. */
export function clampForecastCacheKey(startMonth: string, endMonthExclusive: string): string {
  return `${startMonth}|${endMonthExclusive}`;
}

/** Cache key segment for hub-cache SSE (`mrp.forecast:<key>`); mirrors server `mrpForecastCacheKey`. */
export function mrpForecastCacheKey(options: {
  startMonth: string;
  horizon: number;
  locations: string[];
}): string {
  const locations = [...options.locations].sort().join(",");
  return `${options.startMonth}|${options.horizon}|${locations}`;
}

/** Cache key segment for hub-cache SSE (`mrp.purchase_orders:<key>`); mirrors server `mrpPurchaseOrderListCacheKey`. */
export function mrpPurchaseOrderListCacheKey(options: {
  status?: string;
  draft?: string;
  location?: string;
  cpn?: string;
  view?: "shell" | "full";
  viewerUserId?: string;
}): string {
  const status = (options.status ?? "all").trim() || "all";
  const draft = (options.draft ?? "all").trim() || "all";
  const view = options.view === "shell" ? "shell" : "full";
  const cpn = (options.cpn ?? "").trim().toLowerCase();
  const viewer = draft === "my_drafts" ? (options.viewerUserId ?? "").trim() : "";
  const locations = (options.location ?? "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter((value) => value.length > 0)
    .sort()
    .join(",");
  return `${status}|${draft}|${locations}|${cpn}|${view}|${viewer}`;
}

/** Cache key segment for hub-cache SSE (`mrp.planning.order_lines_by_quarter:<key>`). */
export function mrpPlanningOrderLinesCacheKey(options: {
  pastQuarters?: number;
  includeCurrentQuarter?: boolean;
  fromDate?: string;
  toExclusive?: string;
  variableName?: string;
}): string {
  const variable =
    typeof options.variableName === "string" && options.variableName.trim() !== ""
      ? options.variableName.trim()
      : "topLevel";
  const fromDate = options.fromDate?.trim() ?? "";
  const toExclusive = options.toExclusive?.trim() ?? "";
  if (fromDate !== "" && toExclusive !== "") {
    return `range|${fromDate}|${toExclusive}|${variable}`;
  }
  const include = options.includeCurrentQuarter === true ? "1" : "0";
  return `past|${String(options.pastQuarters ?? 5)}|${include}|${variable}`;
}

export interface MrpOverstockRow {
  cpn: string;
  display_name: string | null;
  on_hand: number;
  on_order: number;
  supply_position: number;
  avg_monthly_demand: number;
  months_of_supply: number | null;
  months_to_reorder: number | null;
  horizon_ending_qty: number;
  overstock_qty: number;
  /** Latest purchase unit price in USD (CNY purchase orders converted at PO date). */
  unit_price: number | null;
  unit_price_currency: string | null;
  unit_price_source_amount: number | null;
  on_hand_value: number | null;
  on_order_value: number | null;
  /** Combined supply-position value: on-hand plus on-order at unit_price. */
  overstock_value: number | null;
  has_zero_forecast_demand: boolean;
}

export interface MrpOverstockResult {
  start_month: string;
  end_month: string;
  horizon_months: number;
  rows: MrpOverstockRow[];
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
}

/** Cache key segment for hub-cache SSE (`mrp.overstock:<key>`); mirrors server `overstockCacheKey`. */
export function mrpOverstockCacheKey(options: {
  startMonth: string;
  horizon: number;
  candidatesOnly: boolean;
}): string {
  return `${options.startMonth}|${options.horizon}|${options.candidatesOnly ? "1" : "0"}`;
}

export interface MrpForecastSourcingSupplier {
  approved_component_id: number;
  supplier_data_id: number;
  mv_supplier_name: string | null;
  channel: "catalog_web" | "contract_manufacturer";
}

export interface MrpForecastSourcingAmlRow {
  id: number;
  manufacturer_name: string | null;
  mpn: string;
  is_placeholder: boolean;
}

export interface MrpForecastSourcingCpnSummary {
  aml_rows: MrpForecastSourcingAmlRow[];
  suppliers: MrpForecastSourcingSupplier[];
}

export function uniqueMrpForecastSupplierNames(
  summary: MrpForecastSourcingCpnSummary | undefined,
): string[] {
  if (summary == null) return [];
  const names = new Set<string>();
  for (const row of summary.suppliers) {
    const label = row.mv_supplier_name?.trim();
    if (label) names.add(label);
  }
  return [...names].sort((left, right) => left.localeCompare(right));
}

export function mrpForecastUniqueSupplierCount(
  summary: MrpForecastSourcingCpnSummary | undefined,
): number {
  return uniqueMrpForecastSupplierNames(summary).length;
}

export function formatMrpForecastSupplierHoverHint(
  summary: MrpForecastSourcingCpnSummary | undefined,
): string {
  const names = uniqueMrpForecastSupplierNames(summary);
  if (names.length === 0) return "No sourcing vendors yet. Click to add.";
  return names.join(" · ");
}

export function cpnMatchesMrpForecastSupplierFilter(
  summary: MrpForecastSourcingCpnSummary | undefined,
  selectedSupplierNames: ReadonlySet<string>,
  allSupplierNames: ReadonlySet<string>,
): boolean {
  if (selectedSupplierNames.size === 0) return false;
  if (allSupplierNames.size > 0 && selectedSupplierNames.size >= allSupplierNames.size) return true;
  return uniqueMrpForecastSupplierNames(summary).some((name) => selectedSupplierNames.has(name));
}

/** Mirrors server `forecastSourcingNeedsPlaceholderEnsure` — empty summary must refetch, not use forecast cache. */
export function mrpForecastSourcingNeedsPlaceholderEnsure(
  summary: MrpForecastSourcingCpnSummary | undefined,
): boolean {
  if (summary == null) return true;
  if (summary.aml_rows.length === 0) return true;
  const realCount = summary.aml_rows.filter((row) => !row.is_placeholder).length;
  const placeholderCount = summary.aml_rows.filter((row) => row.is_placeholder).length;
  return realCount === 0 && placeholderCount > 1;
}

/**
 * BOM `variable_name` where FI-*-TLA-* (**TLA**) assemblies resolve per BoM shipping rules (`docs/bom-system-complete.md`:
 * **`topLevel`** slot, e.g. rule `topLevel` → `FI-3C-B-TLA-A`). The Planning page uses this value only; it is not user-configurable in the UI.
 */
export const MRP_PLANNING_TLA_BOM_VARIABLE_NAME = "topLevel" as const;

/** Single bucket from GET /api/mrp/planning/order-lines-by-quarter */
export interface MrpPlanningQuarterlyLineRow {
  quarter_first_day: string;
  quarter_label: string;
  /** Resolved `component_sku` for the chosen BOM slot from `process_boms`. */
  bom_component_sku: string | null;
  assembly_code: string | null;
  assembly_display_name: string | null;
  quantity_sum: number;
  line_count: number;
}

/** Single SKU row from BoM when `/api/mrp/planning/order-lines-by-quarter` skips that finished good. */
export interface MrpPlanningBomResolutionError {
  sku: string;
  message: string;
}

/** Aggregated totals from GET /api/mrp/planning/order-lines-by-quarter */
export interface MrpPlanningOrderLinesQuarterlyResponse {
  /** Resolved slot (defaults to BOM TLA **`topLevel`** unless overridden). */
  variable_name: string;
  from_inclusive: string;
  to_exclusive: string;
  /** Preset: count of completed quarters before the UTC current quarter; null with explicit `from`/`to` query dates. */
  past_quarters: number | null;
  /** Preset: whether the rolling window also includes the ongoing calendar quarter. */
  include_current_quarter: boolean | null;
  /** BoM/`process_boms` skipped per SKU; order lines roll up Unknown for those SKUs while the window still aggregates. */
  bom_resolution_errors: MrpPlanningBomResolutionError[];
  /** Distinct finished-good order SKUs with no resolved topLevel TLA in the window. */
  unknown_order_skus: string[];
  rows: MrpPlanningQuarterlyLineRow[];
}

export interface MrpAppSetting {
  key: string;
  value: unknown;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** Written by inventory ingest only; shown read-only on MRP settings. */
export const MRP_APP_SETTING_INVENTORY_SNAPSHOT_LAST_FULL_SYNC_AT =
  "inventory_snapshot_last_full_sync_at";

/** Row from GET /api/mrp/aml-sku-options (public.sku) */
export interface MrpSkuOption {
  sku: string;
  display_name: string | null;
}

/** AML change request including resolution — GET /api/mrp/aml-requests/history?cpn= */
export interface MrpAmlChangeRequestHistoryRow {
  id: number;
  created_at: string;
  requested_by: string;
  requester_display_name: string;
  approved_component_id: number | null;
  action: string;
  payload: {
    notes?: string | null;
    manufacturer_name?: string | null;
    mpn?: string | null;
    item_source?: string | null;
  };
  status: string;
  cpn: string;
  category: string | null;
  description: string | null;
  manufacturer_name: string | null;
  mpn: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolver_display_name: string | null;
  resolution_notes: string | null;
}

/** Pending AML change — GET /api/mrp/aml-requests */
export interface MrpAmlChangeRequestListRow {
  id: number;
  created_at: string;
  /** Email stored on the queue row (same as submitted at request time). */
  requested_by: string;
  /** public.user_setting.display_name when the requester matches auth.users, else email. */
  requester_display_name: string;
  /** Target row in public.approved_components (not the queue row id in column id). */
  approved_component_id: number | null;
  action: string;
  payload: {
    notes?: string | null;
    manufacturer_name?: string | null;
    mpn?: string | null;
    item_source?: string | null;
  };
  status: string;
  cpn: string;
  category: string | null;
  description: string | null;
  manufacturer_name: string | null;
  mpn: string;
}

/** GET /api/mrp/aml list response */
export interface MrpAmlListResponse {
  rows: MrpAmlEntry[];
  sku_hover_hints: Record<string, MrpSkuHoverHintFields>;
  total_cpns: number;
  total_pages: number;
}

/** One AML row from public.approved_components — GET /api/mrp/aml */
export interface MrpAmlEntry {
  id: number;
  cpn: string;
  /** Megaventory product category short name (e.g. PCBA). */
  category: string | null;
  /** AML part / MV description and MV variant, space-separated. */
  description: string | null;
  manufacturer_name: string | null;
  mpn: string;
  /** Label or catalog spellings that inbound receive should accept for this AML row. */
  mpn_aliases: string[];
  /** Whole units per supplier pack or reel on this AML line. */
  standard_pack_size: number | null;
  /** Set on insert only; not patchable from the AML UI (not workflow request_source). */
  item_source: string | null;
  notes: string | null;
  /** Listing flag only. Orthogonal to status. false hides from the default AML list. */
  active: boolean;
  /** Inclusive last valid date (YYYY-MM-DD). Set with status deprecated. */
  end_date: string | null;
  updated_at: string;
  updated_by: string | null;
  /** Approval lifecycle: pending_review, approved, rejected, deprecated. Orthogonal to active. */
  status: string;
  /** Legacy AML workflow: where the approval request came from. */
  request_source: string | null;
  request_context: string | null;
  /** bigint in database; may serialize as string from Postgres. */
  requested_by: string | number | null;
  requested_at: string | null;
  supporting_docs: string[] | null;
}

/** Viewed price tier on Shopping listing — GET /api/mrp/shopping/cpn/:cpn */
export interface MrpShoppingPriceBreak {
  id: number;
  break_quantity: number;
  unit_price: number;
  currency: string;
  observed_at: string;
  note: string | null;
}

/** AML × supplier listing — GET /api/mrp/shopping/cpn/:cpn */
export interface MrpShoppingListing {
  id: number;
  approved_component_id: number;
  supplier_data_id: number;
  product_url: string | null;
  purchaser_notes: string | null;
  is_preferred_for_buy: boolean;
  offers_product_observed_at: string | null;
  availability_note: string | null;
  updated_at: string;
  price_breaks: MrpShoppingPriceBreak[];
}

export interface MrpShoppingOrgPurchaseLine {
  cpn: string;
  unit_price: number | null;
  order_quantity: number | null;
  purchase_order_id: number;
  purchase_order_item_id: number;
  mv_number: string | null;
  mv_id: number | null;
  purchase_order_created: string | null;
  purchase_order_updated: string | null;
  supplier_data_id: number | null;
  supplier_name: string | null;
}

export interface MrpShoppingSupplierChannelRow {
  id: number;
  mv_supplier_id: number;
  mv_supplier_name: string | null;
  channel: "catalog_web" | "contract_manufacturer" | null;
}

/** Per-AML Megaventory supplier link — GET /api/mrp/shopping/cpn/:cpn and AML sourcing endpoints. */
export interface MrpAmlSourcingSupplierLink {
  approved_component_id: number;
  /** supplier.data id */
  id: number;
  mv_supplier_id: number;
  mv_supplier_name: string | null;
  channel: "catalog_web" | "contract_manufacturer";
}

/** Per-alternate Shopping matrix options — GET /api/mrp/shopping/cpn/:cpn */
export interface MrpAmlShoppingPref {
  approved_component_id: number;
  show_octopart: boolean;
}

export interface MrpShoppingCpnResponse {
  cpn: string;
  aml_rows: MrpAmlEntry[];
  aml_suppliers: MrpAmlSourcingSupplierLink[];
  /** Present after migration 043 / current API; treat missing as []. */
  aml_shopping_prefs?: MrpAmlShoppingPref[];
  listings: MrpShoppingListing[];
  org_buying_history: {
    last_purchase: MrpShoppingOrgPurchaseLine | null;
    recent_lines: MrpShoppingOrgPurchaseLine[];
  };
  gaps: string[];
}

/** POST /api/mrp/shopping/enrich-from-octopart — Octopart-backed distributor discovery */
export interface MrpShoppingEnrichFromOctopartResult {
  dry_run: boolean;
  batches: number;
  applied: Array<{
    approved_component_id: number;
    supplier_data_id: number;
    distributor_label: string;
    product_url: string | null;
    octopart_url: string | null;
  }>;
  skipped: Array<{ approved_component_id: number; reason: string }>;
  warnings: string[];
}

/** POST /api/mrp/shopping/discover-distributor-links — DigiKey/Mouser APIs plus Octopart */
export interface MrpShoppingDiscoverDistributorLinksResult {
  dry_run: boolean;
  direct: {
    dry_run: boolean;
    digikey_configured: boolean;
    mouser_configured: boolean;
    applied: Array<{
      approved_component_id: number;
      supplier_data_id: number;
      distributor_label: string;
      product_url: string | null;
      price_breaks_added: number;
      source: "digikey-api" | "mouser-api";
    }>;
    skipped: Array<{
      approved_component_id: number;
      distributor_label: string;
      reason: string;
    }>;
    warnings: string[];
  };
  octopart: MrpShoppingEnrichFromOctopartResult | null;
  warnings: string[];
}

export interface MrpShoppingStagedLine {
  id: number;
  cpn: string;
  supplier_data_id: number;
  approved_component_id: number;
  quantity: number;
  unit_price: number | null;
  created_at: string;
  mv_supplier_name: string | null;
}

export function firstOfThisMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

export function formatMonth(monthIso: string): string {
  const date = new Date(monthIso);
  if (Number.isNaN(date.getTime())) return monthIso;
  return date.toLocaleString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function formatPoMoney(amount: number | null | undefined, currency?: string | null): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const code = currency?.trim();
  return code ? `${code} ${formatted}` : formatted;
}

export function poCurrencySymbol(currency?: string | null): string {
  const code = (currency?.trim() || "USD").toUpperCase();
  try {
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(1);
    return parts.find((part) => part.type === "currency")?.value ?? code;
  } catch {
    return code;
  }
}

export function formatPoMoneyWithSymbol(
  amount: number | null | undefined,
  currency?: string | null,
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return `${poCurrencySymbol(currency)}${formatted}`;
}

export async function addPurchaseOrderFreshdeskTicket(
  purchaseOrderId: number,
  ticketId: number,
): Promise<number[]> {
  const response = await apiFetch<{ freshdesk_ticket_ids: number[] }>(
    `/api/mrp/purchase-orders/${purchaseOrderId}/freshdesk-tickets`,
    { method: "POST", body: { ticketId } },
  );
  return response.freshdesk_ticket_ids;
}

export async function removePurchaseOrderFreshdeskTicket(
  purchaseOrderId: number,
  ticketId: number,
): Promise<number[]> {
  const response = await apiFetch<{ freshdesk_ticket_ids: number[] }>(
    `/api/mrp/purchase-orders/${purchaseOrderId}/freshdesk-tickets/${ticketId}`,
    { method: "DELETE" },
  );
  return response.freshdesk_ticket_ids;
}
